@let facts = view();

<div class="flex h-full min-h-0 flex-col gap-4 overflow-y-auto">
    <div class="grid gap-4 lg:grid-cols-3">
        <article class="overflow-hidden rounded-xl border border-border bg-card shadow-sm lg:col-span-2">
            <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-muted/50 px-4 py-3 dark:border-white/10">
                <div>
                    <h2 class="text-sm font-semibold text-primary">The monitoring brief</h2>
                    <p class="mt-0.5 text-xs text-secondary">What this brief is watching for.</p>
                </div>
                @if (facts.documentUrl; as documentUrl) {
                <a class="text-sm font-semibold text-primary underline" [href]="documentUrl" target="_blank"
                    rel="noopener noreferrer">Brief document</a>
                }
            </div>

            <div class="px-5 py-4">
                @if (facts.briefText) {
                <div class="text-sm leading-relaxed text-foreground/80 [&_li]:ml-4 [&_li]:list-disc"
                    [innerHTML]="facts.briefText"></div>
                }

                @if (facts.requirements) {
                <h3 class="mt-5 text-xs font-bold uppercase tracking-wide text-secondary">Requirements</h3>
                <div class="mt-2 text-sm leading-relaxed text-foreground/80 [&_li]:ml-4 [&_li]:list-disc"
                    [innerHTML]="facts.requirements"></div>
                }

                @if (facts.restrictions) {
                <h3 class="mt-5 text-xs font-bold uppercase tracking-wide text-secondary">Restrictions</h3>
                <div class="mt-2 text-sm leading-relaxed text-foreground/80 [&_li]:ml-4 [&_li]:list-disc"
                    [innerHTML]="facts.restrictions"></div>
                }

                @if (facts.createdLabel && facts.postedBy) {
                <p class="mt-5 text-sm text-secondary">Posted {{ facts.createdLabel }} by {{ facts.postedBy }}</p>
                } @else if (facts.createdLabel) {
                <p class="mt-5 text-sm text-secondary">Posted {{ facts.createdLabel }}</p>
                } @else if (facts.postedBy) {
                <p class="mt-5 text-sm text-secondary">Posted by {{ facts.postedBy }}</p>
                }
            </div>

            @if (facts.compliance.length) {
            <div class="border-t border-gray-200 px-5 py-4 dark:border-white/10">
                <h3 class="text-xs font-bold uppercase tracking-wide text-secondary">Compliance</h3>
                <ul class="mt-2 flex flex-col gap-2">
                    @for (doc of facts.compliance; track doc.id) {
                    <li>
                        @if (doc.url) {
                        <a class="text-sm font-semibold text-primary underline" [href]="doc.url" target="_blank"
                            rel="noopener noreferrer">{{ doc.name }}</a>
                        } @else {
                        <p class="text-sm text-primary">{{ doc.name }}</p>
                        }
                    </li>
                    }
                </ul>
            </div>
            }
        </article>

        <aside class="flex flex-col gap-3">
            @if (facts.productName || facts.productImage) {
            <section class="rounded-xl border border-border bg-card p-4 shadow-sm">
                <h2 class="text-xs font-bold uppercase tracking-wide text-secondary">Product</h2>
                <div class="mt-3 flex items-center gap-3">
                    @if (facts.productImage) {
                    <div class="size-10 shrink-0 overflow-hidden rounded-md">
                        <atom-ldg-img inputClass="object-cover" w="full" radius="md"
                            [src]="imageService.getImageSource(facts.productImage, 'gallery')"></atom-ldg-img>
                    </div>
                    }
                    @if (facts.productName) {
                    <p class="min-w-0 truncate text-base font-bold text-primary">{{ facts.productName }}</p>
                    }
                </div>
            </section>
            }

            <section class="rounded-xl border border-border bg-card p-4 shadow-sm">
                <h2 class="text-xs font-bold uppercase tracking-wide text-secondary">Monitoring type</h2>
                <p class="mt-2 text-xl font-bold text-primary">{{ facts.monitorType }}</p>
            </section>
        </aside>
    </div>

    @if (facts.window; as window) {
    <section class="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div class="border-b border-gray-200 bg-muted/50 px-4 py-3 dark:border-white/10">
            <h2 class="text-sm font-semibold text-primary">Monitoring window</h2>
            <p class="mt-0.5 text-xs text-secondary">Posts are watched from the start date through the end date.</p>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3 p-4">
            <div class="flex items-center gap-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-secondary">From</p>
                    <p class="text-base font-bold text-primary">{{ window.from }}</p>
                </div>
                <p class="text-secondary" aria-hidden="true">→</p>
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-secondary">To</p>
                    <p class="text-base font-bold text-primary">{{ window.to }}</p>
                </div>
            </div>
            <p class="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-secondary">{{ window.days }}
                {{ window.days === 1 ? 'day' : 'days' }}</p>
        </div>
    </section>
    }

    @if (findsContentOnly()) {
    <section class="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
        aria-labelledby="find-content-heading">
        <div class="flex items-center gap-x-2 border-b border-gray-200 bg-muted/50 px-3 py-3 dark:border-white/10">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="size-5 shrink-0 text-accent" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
            </svg>
            <div>
                <h3 id="find-content-heading" class="text-sm font-semibold text-primary">How Shout handles this brief
                </h3>
                <p class="mt-0.5 hidden text-xs text-secondary sm:block">Find Brief Content</p>
            </div>
        </div>
        <div class="flex flex-col gap-2 px-5 py-4 text-sm leading-relaxed text-foreground/80">
            <p class="m-0">Shout shows all the content that the creators on this brief have posted that matches the
                brief, including Stories.</p>
            <p class="m-0">Shout doesn't check each video's accuracy against the brief, so posts aren't approved,
                flagged with issues or scored for progress.</p>
        </div>
    </section>
    } @else {
    <section>
        <div
            class="flex w-full min-h-0 flex-col divide-y divide-gray-200 overflow-hidden rounded-xl border border-border bg-white shadow dark:bg-gray-800">
            <div class="shrink-0 border-b border-gray-200 bg-muted/50 px-3 py-3 dark:border-white/10">
                <div class="flex flex-row items-center justify-between">
                    <div class="flex flex-1 flex-row items-center justify-start gap-x-2">
                        <div class="flex flex-col items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-accent" aria-hidden="true">
                                <path d="M13 5h8" />
                                <path d="M13 12h8" />
                                <path d="M13 19h8" />
                                <path d="m3 17 2 2 4-4" />
                                <path d="m3 7 2 2 4-4" />
                            </svg>
                        </div>
                        <div class="flex flex-col">
                            <h3 class="text-sm font-semibold text-primary">Rules extracted from the brief</h3>
                            <p class="mt-0.5 hidden text-xs text-secondary sm:block">{{ ruleSummary() }}</p>
                        </div>
                    </div>
                    <div class="flex min-w-0 shrink-0 gap-2 overflow-x-auto scrollbar-transparent" role="group"
                        aria-label="Filter rules">
                        @for (chip of filterChips(); track chip.id) {
                        <button type="button" (click)="setFilter(chip.id)"
                            [attr.aria-pressed]="ruleFilter() === chip.id"
                            [attr.aria-label]="filterAccessibleLabel(chip)"
                            [class]="filterChipClass(chip.id)">
                            {{ chip.label }}
                            <span aria-hidden="true" [class]="filterCountClass(chip.id)">{{ chip.count }}</span>
                        </button>
                        }
                    </div>
                </div>
            </div>
            <div class="p-4">
                @if (rulesLoading()) {
                <p class="m-0 text-sm text-secondary">Loading rules…</p>
                } @else if (visibleRules().length) {
                <div class="grid gap-3 md:grid-cols-2">
                    @for (entry of visibleRules(); track entry.rule.id) {
                    <article class="flex gap-3 rounded-md border border-border bg-card p-2 shadow-xs border-l-4"
                        [class.border-l-green-600]="entry.rule.type === 'do'"
                        [class.border-l-red-600]="entry.rule.type !== 'do'">
                        <span [class]="ruleIconClass(entry.rule)">
                            @if (entry.rule.type === 'do') {
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            } @else {
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                            </svg>
                            }
                        </span>
                        <div class="min-w-0 flex-1">
                            <p class="m-0 text-pretty text-sm leading-relaxed text-foreground">
                                <span class="sr-only">{{ entry.rule.type === 'do' ? 'Do: ' : "Don't: " }}</span>{{
                                entry.rule.statement }}
                            </p>
                            <div class="mt-2 flex flex-wrap items-center gap-1.5">
                                <span
                                    class="rounded bg-foreground px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-background">
                                    {{ entry.rule.questionSubType }}
                                </span>
                                <span [class]="ruleKindClass(entry.rule)">
                                    {{ entry.rule.subjectiveType === 1 ? 'Subjective' : 'Objective' }}
                                </span>
                                <span
                                    class="rounded border border-border px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                    {{ ruleTag(entry.rule) }}
                                </span>

                                @if (entry.rule.generalised && hasContext(entry.rule)) {
                                <span
                                    class="ml-auto inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-500/15 dark:text-green-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-3.5" aria-hidden="true">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                    Context added
                                </span>
                                } @else if (entry.rule.generalised) {
                                <span
                                    class="ml-auto inline-flex items-center gap-x-1.5 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-400/10 dark:text-yellow-500">
                                    <svg viewBox="0 0 6 6" aria-hidden="true"
                                        class="size-1.5 fill-yellow-500 dark:fill-yellow-400">
                                        <circle r="3" cx="3" cy="3" />
                                    </svg>
                                    Generalised
                                </span>
                                }
                                <span class="font-mono text-xs text-muted-foreground"
                                    [class.ml-auto]="!entry.rule.generalised">{{
                                    ruleNumberLabel(entry.number) }}</span>
                            </div>
                        </div>
                    </article>
                    }
                </div>
                } @else {
                <p
                    class="flex items-center justify-center gap-2.5 rounded-xl border border-dashed border-border p-8 text-sm text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="m13.5 8.5-5 5" />
                        <path d="m8.5 8.5 5 5" />
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                    </svg>
                    No rules match this filter.
                </p>
                }
            </div>
        </div>
    </section>
    }
</div>
